import { Dir } from '@angular/cdk/bidi';
import { Component, inject, output, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatListOption, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { MatMenu, MatMenuContent, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { EnpsComponent } from '@common/components/base/enps-component';
import { ViewStateFilterStates } from '@common/enums/viewstate-filter-states';
import { ViewStateFilterVisibility } from '@common/enums/viewstate-filter-visibility';
import { ViewStateLayouts } from '@common/enums/viewstate-layouts';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { LanguageSettings } from '@common/services/language-settings';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { Store } from '@ngrx/store';
import { FilterTypes } from '@src/suite/common/enums/filter-types';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { ViewStateService } from '@src/suite/common/services/view-state-service';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { selectIsManagePublicationChannels, selectIsManageTeamMembers, selectIsPlanningViews } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import {
    selectIsViewStateSortingManual,
    selectNavigationViewStateAllFilters,
    selectNavigationViewStateFilter,
    selectNavigationViewStateOptionalFilters,
    selectNavigationViewStateOptionalFiltersIDs,
    selectNavigationViewStateRequiredFilters,
    selectNavigationViewStateRequiredFiltersIDs,
    selectViewHasFilter,
    selectViewStateFilterLabel,
} from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { AppService } from '@suite/app.service';

import { ClickStopPropagationDirective } from '../../../directives/stop-propagation';
import { FilterListComponent } from '../filter-list/filter-list';
import { FilterManageTeamMembersComponent } from '../filter-manage-team-members/filter-manage-team-members';
import { FilterPlanningViewsComponent } from '../filter-planning-views/filter-planning-views';

@Component({
    selector: 'st-filter',
    imports: [
        ClickStopPropagationDirective,
        Dir,
        FilterListComponent,
        FilterManageTeamMembersComponent,
        FilterPlanningViewsComponent,
        FormsModule,
        MatButton,
        MatButtonToggle,
        MatButtonToggleGroup,
        MatIcon,
        MatIconButton,
        MatListOption,
        MatMenu,
        MatMenuContent,
        MatMenuTrigger,
        MatSelectionList,
        MatTooltip,
    ],
    templateUrl: './filter.html',
    styleUrl: './filter.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-filter',
    },
})
export class FilterComponent extends EnpsComponent {
    appService = inject(AppService);
    viewStateService = inject(ViewStateService);
    languageSettings = inject(LanguageSettings);
    nomWebServicesJson = inject(NomWebServicesJson);
    visualStateService = inject(VisualStateService);
    navigationService = inject(NavigationService);
    globalTablesService = inject(GlobalTablesService);
    private store = inject(Store);

    public readonly closeFilter = output<void>();

    public viewStateTypes = ViewStateTypes;
    public viewStateFilterStates = ViewStateFilterStates;
    public viewStateLayouts = ViewStateLayouts;
    public viewStateFilterVisibility = ViewStateFilterVisibility;

    public PSTWebFirstX: string;

    public filterTypes = FilterTypes;
    public filterType: number = FilterTypes.REQUIRED;

    isPlanningViews = this.store.selectSignal(selectIsPlanningViews);
    isManageTeamMembers = this.store.selectSignal(selectIsManageTeamMembers);
    isManagePublicationChannels = this.store.selectSignal(selectIsManagePublicationChannels);

    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    viewStateFilter = this.store.selectSignal(selectNavigationViewStateFilter);
    viewStateAllFilters = this.store.selectSignal(selectNavigationViewStateAllFilters);
    viewStateRequiredFilters = this.store.selectSignal(selectNavigationViewStateRequiredFilters);
    viewStateRequiredFiltersIDs = this.store.selectSignal(selectNavigationViewStateRequiredFiltersIDs);
    viewStateOptionalFilters = this.store.selectSignal(selectNavigationViewStateOptionalFilters);
    viewStateOptionalFiltersIDs = this.store.selectSignal(selectNavigationViewStateOptionalFiltersIDs);
    isViewStateSortingManual = this.store.selectSignal(selectIsViewStateSortingManual);

    viewStateFilterLabel = this.store.selectSignal(selectViewStateFilterLabel);

    viewHasFilter = this.store.selectSignal(selectViewHasFilter);

    constructor() {
        super();
        this.PSTWebFirstX = this.languageSettings.PSTWebFirstX;
    }

    toggle() {
        this.store.dispatch(ViewStateActions.toggleFilterState());
    }

    onFilterStateChange(event: MatSelectionListChange, required: boolean) {
        const added = event.options?.filter(o => o.selected).map(o => o.value) || [];
        const removed = event.options?.filter(o => !o.selected).map(o => o.value) || [];
        const filters = event.source?.selectedOptions?.selected?.map(x => x.value) || [];
        this.store.dispatch(ViewStateActions.addViewStateFilters({ required, filters, added, removed }));
    }

    onRemoveFilter(item: StorytellingFilterDefinition, type: FilterTypes) {
        this.store.dispatch(ViewStateActions.removeViewStateFilter({ required: type === FilterTypes.REQUIRED, filter: item.ID, removed: [item.ID] }));
    }

    onUpdateFilter(filter: StorytellingFilterDefinition) {
        this.store.dispatch(ViewStateActions.updateViewStateFilter({ filter }));
    }

    onAdjustFilter(filter: StorytellingFilterDefinition) {
        this.store.dispatch(ViewStateActions.adjustViewStateFilter({ filter }));
    }

    toggleFilterElevation() {
        this.store.dispatch(ViewStateActions.toggleFilterElevation());
    }
}
